export const FileKind = {
  PDF: 'pdf',
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  PRESENTATION: 'presentation',
  DOCUMENT: 'document',
  OTHER: 'other',
} as const;
export type FileKind = (typeof FileKind)[keyof typeof FileKind];

const fileKindByExtension: Record<string, FileKind> = {
  pdf: FileKind.PDF,
  png: FileKind.IMAGE,
  jpg: FileKind.IMAGE,
  jpeg: FileKind.IMAGE,
  gif: FileKind.IMAGE,
  webp: FileKind.IMAGE,
  mp4: FileKind.VIDEO,
  mov: FileKind.VIDEO,
  ogg: FileKind.VIDEO,
  webm: FileKind.VIDEO,
  mpeg: FileKind.VIDEO,
  mpg: FileKind.VIDEO,
  mp3: FileKind.AUDIO,
  wav: FileKind.AUDIO,
  m4a: FileKind.AUDIO,
  aac: FileKind.AUDIO,
  ppt: FileKind.PRESENTATION,
  pptx: FileKind.PRESENTATION,
  doc: FileKind.DOCUMENT,
  docx: FileKind.DOCUMENT,
};

export const getFileNameExtension = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex === -1 || dotIndex === fileName.length - 1) return '';
  return fileName.slice(dotIndex + 1).toLowerCase();
};

export const getFileKindByExtension = (extension: string): FileKind =>
  fileKindByExtension[extension.toLowerCase()] ?? FileKind.OTHER;

export const getFileKind = (fileName: string): FileKind =>
  getFileKindByExtension(getFileNameExtension(fileName));

export type FileThumbnailSources = {
  fileName: string;
  extension?: string;
  customPreviewUrl?: string | null;
  previewUrl?: string | null;
  fileUrl?: string | null;
};

export const getFileThumbnailKind = ({
  fileName,
  extension,
}: FileThumbnailSources): FileKind =>
  extension ? getFileKindByExtension(extension) : getFileKind(fileName);

export const getFileThumbnailImageUrls = (
  sources: FileThumbnailSources,
): string[] =>
  [
    sources.customPreviewUrl,
    sources.previewUrl,
    getFileThumbnailKind(sources) === FileKind.IMAGE ? sources.fileUrl : null,
  ].filter((url): url is string => typeof url === 'string' && url.length > 0);

export const buildDownloadFileName = (
  title: string,
  sourceFileName: string,
): string => {
  const name = title.trim();
  if (!name) return sourceFileName;
  const extension = getFileNameExtension(sourceFileName);
  if (!extension || name.toLowerCase().endsWith(`.${extension}`)) return name;
  return `${name}.${extension}`;
};

export const getFileNameFromUrl = (fileUrl: string): string => {
  let segment: string;
  try {
    segment = new URL(fileUrl).pathname.split('/').at(-1) ?? '';
  } catch {
    return '';
  }
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

export const formatFileSize = (bytes: number): string => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent =
    bytes <= 0
      ? 0
      : Math.min(
          Math.floor(Math.log(bytes) / Math.log(1024)),
          units.length - 1,
        );
  const value = bytes / 1024 ** exponent;
  const rounded = Math.round(value * 10) / 10;
  return `${rounded} ${units[exponent]}`;
};

export const formatFileDate = (isoString: string): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(isoString));
